'use client';

import type React from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import useRealtimeChats from '@util/hooks/useRealtimeChats';
import { useAuth } from './AuthContext';
import type { ChatDocument } from 'models/chat';
import type { SupportState } from 'models/chat';
import type { OfferDocument } from 'models/offer';
import type { UserDocument } from 'models/user';
import {
  getOffersByIds,
  getOffersByState,
  getOfferStateNum,
} from '@util/firestore/offers';
import { useConversationMutations } from '@util/hooks/useConversationMutations';
import {
  getEmptyStateContent,
  calculateUnreadCounts,
  extractOfferIds,
  SEARCH_DEBOUNCE_MS,
  filterChatsByOfferState,
} from '@util/conversations/conversationUtils';
import useDebounce from '@util/hooks/useDebounce';
import {
  getChatsByIds,
  getChatsBySearchQuery,
  getChatsQuery,
  MAIN_FILTERS,
} from '@util/firestore/messages';
import type { MainFilters } from '@util/firestore/messages';
import { getDocs, type QueryDocumentSnapshot } from 'firebase/firestore';

interface ConversationsContextType {
  user: UserDocument | null | undefined;
  userDoc: UserDocument | null | undefined;

  markAsRead: {
    mutate: (chat_id: string) => void;
    isPending: boolean;
  };
  updateSupportState: {
    mutate: (data: { chat_id: string; state: SupportState }) => void;
    isPending: boolean;
  };
  assignTicket: {
    mutate: (data: { chat_id: string; uid: string }) => void;
    isPending: boolean;
  };
  markAsUnread: {
    mutate: (chat_id: string) => void;
    isPending: boolean;
  };
  archiveChat: {
    mutate: (chat_id: string, options?: { onSuccess?: () => void }) => void;
    isPending: boolean;
  };
  starChat: {
    mutate: (chat_id: string) => void;
    isPending: boolean;
  };
  getEmptyStateContent: (filterValue: MainFilters) => {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
  };
}

export interface UseConversationsReturn extends ConversationsContextType {
  chats: ChatDocument[];
  filteredChats: ChatDocument[];
  availableFilters: ReadonlyArray<{ label: string; value: MainFilters }>;
  searchQuery: string;
  debouncedSearchQuery: string;
  setSearchQuery: (query: string) => void;
  activeFilter: MainFilters;
  setActiveFilter: (filter: MainFilters) => void;
  unreadCounts: Record<string, number>;
  realtimeChats: ChatDocument[] | undefined;
  offers: OfferDocument[] | undefined;
  isLoading: boolean;
  isFilteredChatsLoading: boolean;
  isFiltered: boolean;
  onLoadMore: () => Promise<void>;
  isLoadingMore: boolean;
  shouldEnableLoadMore: boolean;
  lastFetchedChatsCount: number | undefined;
  unreadCount: number;
  setPreviouslySelectedUnreadChat: (chatId: string | null) => void;
}

const ConversationsContext = createContext<
  ConversationsContextType | undefined
>(undefined);

export const useConversations = ({
  chats,
}: {
  chats?: ChatDocument[];
}): UseConversationsReturn => {
  const context = useContext(ConversationsContext);
  const [isFilteredChatsLoading, setIsFilteredChatsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<MainFilters>('all');
  const [lastFetchedChatsCount, setLastFetchedChatsCount] = useState<
    number | undefined
  >(undefined);
  const [lastLoadedChat, setLastLoadedChat] =
    useState<QueryDocumentSnapshot<ChatDocument> | null>(null);
  const [previouslySelectedUnreadChatId, setPreviouslySelectedUnreadChatId] =
    useState<string | null>(null);
  const { userDoc } = useAuth();

  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);

  if (!context) {
    throw new Error(
      'useConversations must be used within ConversationsProvider'
    );
  }

  const { realtimeChats, isLoading } = useRealtimeChats(userDoc, undefined);
  const { realtimeChats: offerChats } = useRealtimeChats(userDoc, 'offers');

  const [allChats, setAllChats] = useState<ChatDocument[]>(chats ?? []);

  const shouldEnableLoadMore =
    lastFetchedChatsCount === undefined || lastFetchedChatsCount > 0;

  const resetPaginationState = useCallback(() => {
    setLastFetchedChatsCount(undefined);
    setLastLoadedChat(null);
  }, []);

  const handleSetActiveFilter = useCallback(
    (filter: MainFilters) => {
      resetPaginationState();
      setActiveFilter(filter);
      setPreviouslySelectedUnreadChatId(null);
    },
    [resetPaginationState]
  );

  const setPreviouslySelectedUnreadChat = useCallback(
    (chatId: string | null) => {
      setPreviouslySelectedUnreadChatId(chatId);
    },
    []
  );

  const addChatsToAllChats = useCallback(
    (chats: ChatDocument[], updateCount = true) => {
      setAllChats((prevChats) => {
        const chatMap = new Map<string, ChatDocument>();

        prevChats.forEach((chat) => {
          chatMap.set(chat.id, chat);
        });

        chats.forEach((chat) => {
          chatMap.set(chat.id, chat);
        });

        const chatsArray = Array.from(chatMap.values());

        if (updateCount) {
          setLastFetchedChatsCount(chatsArray.length - prevChats.length);
        }

        return chatsArray;
      });
    },
    []
  );

  useEffect(() => {
    if (realtimeChats) {
      addChatsToAllChats(realtimeChats, false);
    }

    if (offerChats) {
      addChatsToAllChats(offerChats, false);
    }
  }, [realtimeChats, offerChats, addChatsToAllChats]);

  const offerIds = useMemo(() => {
    return extractOfferIds(allChats);
  }, [allChats]);

  const { data: offers } = useQuery({
    queryKey: ['offers', ...offerIds],
    queryFn: () => getOffersByIds(offerIds),
    enabled: offerIds.length > 0,
  });

  const availableFilters = useMemo(() => {
    return Object.keys(MAIN_FILTERS)
      .filter((key) => {
        if (key === 'offer_states') return false;
        if (
          key === 'flagged' &&
          !userDoc?.roles?.admin &&
          !userDoc?.roles?.support
        )
          return false;
        return true;
      })
      .map((key) => ({
        label: MAIN_FILTERS[key as keyof typeof MAIN_FILTERS] as string,
        value: key as MainFilters,
      }));
  }, [userDoc?.roles]);

  const unreadCounts = useMemo(() => {
    return calculateUnreadCounts(
      [...(allChats ?? []), ...(offerChats ?? [])],
      userDoc
    );
  }, [allChats, offerChats, userDoc]);

  // this function is used to load more chats from firestore when the user scrolls to the bottom of the list
  async function onLoadMore() {
    if (!shouldEnableLoadMore) return;

    setIsLoadingMore(true);

    try {
      const query = getChatsQuery({
        userDoc,
        mainFilter: activeFilter,
        lastDoc: lastLoadedChat ?? undefined,
      });

      const snapshot = await getDocs(query);
      const chats = snapshot.docs.map((doc) => doc.data());

      if (chats.length > 0) {
        addChatsToAllChats(chats, false);
        setLastFetchedChatsCount(chats.length);
        setLastLoadedChat(snapshot.docs[snapshot.docs.length - 1]);
      } else {
        setLastFetchedChatsCount(0);
      }
    } catch (error) {
      console.error('Error loading more chats:', error);
      setLastFetchedChatsCount(0);
      setIsLoadingMore(false);
      return;
    }

    setIsLoadingMore(false);
  }

  const fetchOfferChatsByState = useCallback(async () => {
    const offerStateNum = getOfferStateNum(activeFilter.split('_')[1]);

    const offers = await getOffersByState(userDoc, offerStateNum);

    if (!offers.length) {
      setIsFilteredChatsLoading(false);
      return;
    }

    const chatIds = offers.map((offer) => offer.chat_id ?? '');
    const chats = await getChatsByIds(chatIds, userDoc);

    setIsFilteredChatsLoading(false);
    addChatsToAllChats(chats, false);

    return offers;
  }, [activeFilter, userDoc, addChatsToAllChats]);

  useEffect(() => {
    if (activeFilter === 'all') {
      setIsFilteredChatsLoading(false);
      return;
    }

    const isOfferFilter =
      activeFilter.startsWith('offers_') || activeFilter === 'offers';
    const offerState = activeFilter.split('_')[1];

    if (isOfferFilter && offerState && offerState !== 'all') {
      setIsFilteredChatsLoading(true);
      fetchOfferChatsByState();
    } else {
      const loadFilteredChats = async () => {
        const chatsQuery = getChatsQuery({
          userDoc,
          mainFilter: isOfferFilter ? 'offers' : activeFilter,
        });

        if (!chatsQuery) {
          setIsFilteredChatsLoading(false);
          return;
        }

        setIsFilteredChatsLoading(true);

        try {
          const snapshot = await getDocs(chatsQuery);
          const chats = snapshot.docs.map((doc) => doc.data());

          setIsFilteredChatsLoading(false);
          addChatsToAllChats(chats, false);
          if (snapshot.docs.length > 0) {
            setLastLoadedChat(snapshot.docs[snapshot.docs.length - 1]);
          }
        } catch (error) {
          console.error('Error loading filtered chats:', error);
          setIsFilteredChatsLoading(false);
        }
      };

      loadFilteredChats();
    }
  }, [activeFilter, userDoc, fetchOfferChatsByState, addChatsToAllChats]);

  useEffect(() => {
    if (debouncedSearchQuery !== '') {
      const loadSearchChats = async () => {
        setIsFilteredChatsLoading(true);
        const chatsQuery = getChatsBySearchQuery(debouncedSearchQuery, userDoc);

        try {
          const snapshot = await getDocs(chatsQuery);
          const chats = snapshot.docs.map((doc) => doc.data());

          setIsFilteredChatsLoading(false);
          addChatsToAllChats(chats, false);
          if (snapshot.docs.length > 0) {
            setLastLoadedChat(snapshot.docs[snapshot.docs.length - 1]);
          }
        } catch (error) {
          console.error('Error loading search chats:', error);
          setIsFilteredChatsLoading(false);
        }
      };

      loadSearchChats();
    } else {
      setIsFilteredChatsLoading(false);
    }
  }, [debouncedSearchQuery, addChatsToAllChats, userDoc]);

  const filteredChats = useMemo(() => {
    if (activeFilter === 'all' && debouncedSearchQuery === '') {
      return [];
    }

    if (debouncedSearchQuery !== '') {
      return allChats.filter((chat) => {
        const searchLower = debouncedSearchQuery.toLowerCase();
        const sanitizedSearchLower = searchLower.replaceAll('#', '');

        const messageContainsOrderNum = chat.order_num
          ?.toString()
          .includes(sanitizedSearchLower);
        const messageContainsCaseNum = chat.case_num
          ?.toString()
          .includes(sanitizedSearchLower);

        return (
          chat.last_msg?.toLowerCase().includes(searchLower) ||
          chat.messages?.some((msg) =>
            msg.content?.toLowerCase().includes(searchLower)
          ) ||
          messageContainsOrderNum ||
          messageContainsCaseNum
        );
      });
    }

    if (activeFilter === 'offers') {
      return allChats.filter((chat) => chat.offer_id);
    }

    if (activeFilter.startsWith('offers_')) {
      const offerState = activeFilter.split('_')[1];

      if (offerState === 'all' || !offers || offers.length === 0) {
        return allChats.filter((chat) => chat.offer_id);
      }

      return filterChatsByOfferState(allChats, offerState, offers);
    }

    if (activeFilter === 'unread') {
      return allChats.filter((chat) => {
        const userId = userDoc?.uid;
        if (!userId) return false;

        const isUnread = chat.unread[userId];
        const isPreviouslySelected = chat.id === previouslySelectedUnreadChatId;

        // Include chat if it's unread OR if it's the previously selected chat
        return isUnread || isPreviouslySelected;
      });
    }

    if (activeFilter === 'buy') {
      return allChats.filter((chat) => chat.buyer_id === userDoc?.uid);
    }

    if (activeFilter === 'sell') {
      return allChats.filter((chat) => chat.seller_id === userDoc?.uid);
    }

    if (activeFilter === 'support') {
      return allChats.filter((chat) => chat.case_num !== undefined);
    }

    if (activeFilter === 'flagged') {
      return allChats.filter((chat) => chat.flagged === true);
    }

    return allChats;
  }, [
    allChats,
    activeFilter,
    debouncedSearchQuery,
    offers,
    userDoc,
    previouslySelectedUnreadChatId,
  ]);

  const isFiltered = activeFilter !== 'all' || debouncedSearchQuery !== '';

  return {
    ...context,
    chats: allChats,
    filteredChats,
    availableFilters,
    unreadCounts,
    realtimeChats,
    offers,
    isLoading,
    isFilteredChatsLoading,
    searchQuery,
    setSearchQuery,
    activeFilter,
    setActiveFilter: handleSetActiveFilter,
    debouncedSearchQuery,
    isFiltered,
    onLoadMore,
    isLoadingMore,
    shouldEnableLoadMore,
    lastFetchedChatsCount,
    unreadCount: unreadCounts.all,
    setPreviouslySelectedUnreadChat,
  };
};

export const useConversationOperations = () => {
  const context = useContext(ConversationsContext);

  if (!context) {
    throw new Error(
      'useConversationOperations must be used within ConversationsProvider'
    );
  }

  return {
    markAsRead: context.markAsRead,
    markAsUnread: context.markAsUnread,
    updateSupportState: context.updateSupportState,
    assignTicket: context.assignTicket,
    archiveChat: context.archiveChat,
    starChat: context.starChat,
    getEmptyStateContent: context.getEmptyStateContent,
  };
};

export function ConversationsProvider({ children }: { children: ReactNode }) {
  const { userDoc } = useAuth();

  const mutations = useConversationMutations();

  const value: ConversationsContextType = {
    user: userDoc,
    userDoc,
    getEmptyStateContent,
    markAsRead: {
      mutate: (chat_id: string) => mutations.markAsRead.mutate(chat_id),
      isPending: mutations.markAsRead.isPending,
    },
    updateSupportState: {
      mutate: (data: { chat_id: string; state: SupportState }) =>
        mutations.updateSupportState.mutate(data),
      isPending: mutations.updateSupportState.isPending,
    },
    assignTicket: {
      mutate: (data: { chat_id: string; uid: string }) =>
        mutations.assignTicket.mutate(data),
      isPending: mutations.assignTicket.isPending,
    },
    markAsUnread: {
      mutate: (chat_id: string) => mutations.markAsUnread.mutate(chat_id),
      isPending: mutations.markAsUnread.isPending,
    },
    archiveChat: {
      mutate: (chat_id: string, options?: { onSuccess?: () => void }) => {
        mutations.archiveChat.mutate(chat_id, {
          onSuccess: () => {
            options?.onSuccess?.();
          },
        });
      },
      isPending: mutations.archiveChat.isPending,
    },
    starChat: {
      mutate: (chat_id: string) => mutations.starChat.mutate(chat_id),
      isPending: mutations.starChat.isPending,
    },
  };

  return (
    <ConversationsContext.Provider value={value}>
      {children}
    </ConversationsContext.Provider>
  );
}
